import React, { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import api, { fmtDateTime } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";


function blobDownload(res, fallback) {
  const cd = res.headers?.["content-disposition"] || "";
  const m = cd.match(/filename=([^;]+)/);
  const name = m ? m[1].trim() : fallback;
  const url = URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

/**
 * fixedScope: null => Master context (scope picker: universal + modules).
 *             "module:<id>" => scoped to one module (no picker).
 * allowPurge: purge is only available in the Master context (never inside module settings).
 */
export default function DataManagement({ fixedScope = null, allowPurge = false }) {
  const [scope, setScope] = useState(fixedScope || "universal");
  const [backups, setBackups] = useState([]);
  const [busy, setBusy] = useState("");

  // restore
  const [restoreFile, setRestoreFile] = useState(null);
  const [validation, setValidation] = useState(null);

  // purge
  const [purgeScope, setPurgeScope] = useState(fixedScope || "universal");
  const [scopes, setScopes] = useState([]);
  useEffect(() => { api.get("/data/scopes").then(({ data }) => setScopes(data)).catch(() => {}); }, []);
  const [preview, setPreview] = useState(null);
  const [pw, setPw] = useState("");
  const [phrase, setPhrase] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [reason, setReason] = useState("");

  const loadBackups = useCallback(() => {
    api.get("/data/backups").then(({ data }) => {
      setBackups(fixedScope ? data.filter((b) => b.scope === fixedScope) : data);
    }).catch(() => {});
  }, [fixedScope]);
  useEffect(() => { loadBackups(); }, [loadBackups]);

  const createBackup = async () => {
    setBusy("backup");
    try {
      const { data } = await api.post("/data/backup", { scope });
      const total = Object.values(data.meta.counts || {}).reduce((a, b) => a + b, 0);
      toast.success(`Backup created — ${total} records`);
      loadBackups();
    } catch (e) { toast.error(e.response?.data?.detail || "Backup failed"); }
    setBusy("");
  };

  const download = async (b) => {
    try {
      const res = await api.get(`/data/backup/${b.id}/download`, { responseType: "blob" });
      blobDownload(res, `backup-${b.id}.uempbak`);
    } catch (e) { toast.error("Download failed"); }
  };

  const delBackup = async (b) => {
    if (!window.confirm("Delete this backup file?")) return;
    try { await api.delete(`/data/backup/${b.id}`); loadBackups(); } catch (e) { toast.error("Failed"); }
  };

  const validateRestore = async () => {
    if (!restoreFile) { toast.error("Choose a backup file first"); return; }
    setBusy("validate"); setValidation(null);
    try {
      const fd = new FormData(); fd.append("file", restoreFile);
      const { data } = await api.post("/data/restore/validate", fd);
      setValidation(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid backup file"); }
    setBusy("");
  };

  const applyRestore = async () => {
    if (!window.confirm("This will OVERWRITE the collections in the backup. A recovery checkpoint is created first. Continue?")) return;
    setBusy("restore");
    try {
      const fd = new FormData(); fd.append("file", restoreFile); fd.append("confirm", "RESTORE");
      const { data } = await api.post("/data/restore/apply", fd);
      const total = Object.values(data.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Restored ${total} records (checkpoint saved)`);
      setValidation(null); setRestoreFile(null); loadBackups();
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
    setBusy("");
  };

  const runPreview = async () => {
    setPreview(null);
    try {
      const { data } = await api.post("/data/purge/preview", { scope: purgeScope });
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const executePurge = async () => {
    setBusy("purge");
    try {
      const { data } = await api.post("/data/purge", {
        scope: purgeScope, password: pw, confirm_phrase: phrase, mfa_code: mfaCode, reason,
      });
      toast.success(`Purged ${data.total} records (recovery checkpoint saved)`);
      setPreview(null); setPw(""); setPhrase(""); setMfaCode(""); setReason(""); loadBackups();
    } catch (e) { toast.error(e.response?.data?.detail || "Purge blocked"); }
    setBusy("");
  };

  const ScopePicker = ({ value, onChange, purge }) => {
    const cur = scopes.find((s) => s.scope === value);
    const list = cur ? (purge ? cur.purgeable : cur.collections) : [];
    return (
      <div className="space-y-1.5">
        <select className="w-full border rounded-lg h-10 px-3 text-sm" value={value}
          onChange={(e) => onChange(e.target.value)} data-testid={purge ? "purge-scope-select" : "data-scope-select"}>
          {scopes.length === 0 && <option value="universal">Universal — entire platform</option>}
          {scopes.map((s) => <option key={s.scope} value={s.scope}>{s.scope === "universal" ? s.label : `${s.label} (application)`} · {(purge ? s.purgeable : s.collections).length} tables</option>)}
        </select>
        {cur && <details className="text-[11px] text-slate-500"><summary className="cursor-pointer" data-testid={purge ? "purge-scope-tables" : "data-scope-tables"}>{list.length} tables included{purge ? " (users, roles, access, settings and audit logs are always kept)" : ""}</summary><div className="mt-1 font-mono leading-5 break-words">{list.join(", ") || "None"}</div></details>}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {/* Backup */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Icons.DatabaseBackup className="h-5 w-5 text-blue-600" />
          <span className="font-medium text-slate-800">Backup{fixedScope ? "" : " — Universal & Application"}</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1 space-y-2">
            <Label>Scope</Label>
            {fixedScope ? <Input value={fixedScope.replace("module:", "") + " (application)"} disabled /> : <ScopePicker value={scope} onChange={setScope} />}
          </div>
          <Button onClick={createBackup} disabled={busy === "backup"} className="bg-blue-600 hover:bg-blue-700" data-testid="create-backup-btn">
            {busy === "backup" ? "Creating…" : <><Icons.Plus className="h-4 w-4 mr-1.5" /> Create Backup</>}
          </Button>
        </div>
        <div className="mt-4 divide-y divide-slate-100 border-t border-slate-100" data-testid="backups-list">
          {backups.length === 0 && <div className="py-6 text-center text-sm text-slate-400">No backups yet.</div>}
          {backups.map((b) => (
            <div key={b.id} className="py-3 flex items-center gap-3" data-testid="backup-row">
              <Icons.FileArchive className="h-4 w-4 text-slate-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-800 flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] font-mono">{b.scope}</Badge>
                  <span className="text-xs text-slate-400">{(b.size / 1024).toFixed(1)} KB</span>
                </div>
                <div className="text-[11px] text-slate-400">{fmtDateTime(b.created_at)} · {b.created_by}</div>
              </div>
              <button onClick={() => download(b)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" title="Download" data-testid="backup-download"><Icons.Download className="h-4 w-4" /></button>
              <button onClick={() => delBackup(b)} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500" title="Delete" data-testid="backup-delete"><Icons.Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Restore */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Icons.History className="h-5 w-5 text-emerald-600" />
          <span className="font-medium text-slate-800">Restore</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1 space-y-2">
            <Label>Backup file (.uempbak)</Label>
            <Input type="file" onChange={(e) => { setRestoreFile(e.target.files[0]); setValidation(null); }} data-testid="restore-file-input" />
          </div>
          <Button variant="outline" onClick={validateRestore} disabled={busy === "validate"} data-testid="restore-validate-btn">
            {busy === "validate" ? "Validating…" : "Validate"}
          </Button>
        </div>
        {validation && (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/50 p-4" data-testid="restore-summary">
            <div className="text-sm font-medium text-slate-800 mb-2">Validation summary — scope <span className="font-mono">{validation.meta.scope}</span></div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
              {validation.collections.map((c) => (
                <div key={c} className="flex justify-between"><span className="text-slate-500">{c}</span>
                  <span className="text-slate-700">{validation.current_counts[c] ?? 0} → <b>{validation.incoming_counts[c] ?? 0}</b></span></div>
              ))}
            </div>
            <Button onClick={applyRestore} disabled={busy === "restore"} className="mt-4 bg-emerald-600 hover:bg-emerald-700" data-testid="restore-apply-btn">
              {busy === "restore" ? "Restoring…" : "Confirm Restore (overwrite)"}
            </Button>
          </div>
        )}
      </div>

      {/* Purge — Master context only */}
      {allowPurge && (
        <div className="bg-white rounded-xl border border-rose-200 p-5">
          <div className="flex items-center gap-2 mb-1">
            <Icons.AlertTriangle className="h-5 w-5 text-rose-600" />
            <span className="font-medium text-rose-700">Purge — permanent deletion</span>
          </div>
          <p className="text-xs text-slate-500 mb-4">Irreversible. Identity, roles, companies, settings, and audit logs are always preserved. A recovery checkpoint is created before deletion.</p>
          <div className="space-y-3 max-w-xl">
            <div className="space-y-2"><Label>Scope</Label><ScopePicker purge value={purgeScope} onChange={(v) => { setPurgeScope(v); setPreview(null); }} /></div>
            <Button variant="outline" onClick={runPreview} data-testid="purge-preview-btn"><Icons.Search className="h-4 w-4 mr-1.5" /> Preview impact</Button>
            {preview && (
              <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-4 space-y-3" data-testid="purge-preview">
                <div className="text-sm text-slate-800"><b>{preview.total}</b> records across {preview.collections.length} collections will be permanently deleted.</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                  {preview.collections.map((c) => <div key={c} className="flex justify-between"><span className="text-slate-500">{c}</span><span className="text-rose-700">{preview.counts[c]}</span></div>)}
                </div>
                <div className="space-y-2"><Label>Re-enter your password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="purge-password" /></div>
                <div className="space-y-2"><Label>MFA code (if enabled)</Label><Input value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} placeholder="6-digit code" data-testid="purge-mfa" /></div>
                <div className="space-y-2"><Label>Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="purge-reason" /></div>
                <div className="space-y-2"><Label>Type <span className="font-mono text-rose-700">{preview.confirm_phrase}</span> to confirm</Label><Input value={phrase} onChange={(e) => setPhrase(e.target.value)} data-testid="purge-phrase" /></div>
                <Button onClick={executePurge} disabled={busy === "purge" || phrase !== preview.confirm_phrase || !pw}
                  className="bg-rose-600 hover:bg-rose-700" data-testid="purge-execute-btn">
                  {busy === "purge" ? "Purging…" : "Permanently Purge"}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
